import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Activity, TrendingUp, Trophy, Flame, ChevronRight, Dumbbell, PlayCircle } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Legend } from 'recharts';
import DashboardLayout from '../components/DashboardLayout';
import PageHeader from '../components/PageHeader';
import BodyTodayPanel from '../components/BodyTodayPanel';
import MovementWhyPanel from '../components/MovementWhyPanel';
import PillarTrendChart from '../components/PillarTrendChart';
import DistrictEntryButton from '../components/district/DistrictEntryButton';
import ScoreBreakdownPopover from '../components/ScoreBreakdownPopover';
import { buildRegionTrend, TREND_LINES, regionLabel } from '../lib/mobilityRegions';
import api from '../services/api';
import { scoreBucket, toneClass, toneHex } from '../utils/coachLanguage';

// Tiny SVG sparkline used in the movement leaderboard rows.
const Sparkline = ({ values = [], width = 80, height = 24 }) => {
  if (!values.length) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = values.length > 1 ? width / (values.length - 1) : 0;
  const pts = values.map((v, i) => `${(i * step).toFixed(1)},${(height - ((v - min) / range) * height).toFixed(1)}`).join(' ');
  const lastTone = scoreBucket(values[values.length - 1]).tone;
  return (
    <svg width={width} height={height} className="inline-block">
      <polyline fill="none" stroke={toneHex(lastTone)} strokeWidth="1.5" points={pts} />
    </svg>
  );
};

const DeltaPill = ({ value }) => {
  if (value === null || value === undefined) {
    return <span className="text-zinc-500 text-xs">— need 2 sessions</span>;
  }
  const v = Number(value) || 0;
  if (Math.abs(v) < 0.5) return <span className="text-zinc-400 text-xs">→ flat</span>;
  if (v > 0) return <span className="text-green-400 text-xs">↑ +{v.toFixed(1)}</span>;
  return <span className="text-red-400 text-xs">↓ {v.toFixed(1)}</span>;
};

const Performance = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobility, setMobility] = useState(null);
  const [mobilityHistory, setMobilityHistory] = useState([]);
  const [iq, setIq] = useState(null);  // /api/wellness/iq — for Movement IQ + insight + breakdown
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [progRes, mobRes, mobHistRes, iqRes] = await Promise.all([
          api.getPerformanceProgression(),
          api.getCurrentBaseline().catch(() => null),
          api.getBaselineHistory().catch(() => null),
          api.getWellnessIQ().catch(() => null),
        ]);
        setData(progRes.data);
        if (mobRes?.data?.baseline) setMobility(mobRes.data.baseline);
        if (mobHistRes?.data?.baselines) setMobilityHistory(mobHistRes.data.baselines);
        if (iqRes?.data) setIq(iqRes.data);
      } catch (e) {
        console.error('Failed to fetch progression:', e);
      } finally {
        setLoading(false);
      }
    })();
    // Pillar scores + insight refresh when any check-in / session is logged.
    const onRefresh = () => api.getWellnessIQ().then(r => setIq(r.data)).catch(() => {});
    window.addEventListener('wellness:refresh', onRefresh);
    return () => window.removeEventListener('wellness:refresh', onRefresh);
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <Activity className="w-12 h-12 text-primary animate-pulse" />
        </div>
      </DashboardLayout>
    );
  }

  const trend = data?.overall_trend || [];
  const hasData = trend.length > 0;
  const currentAvg = data?.current_period_avg;
  const delta = data?.delta;
  const currentBucket = scoreBucket(currentAvg);
  const leaderboard = data?.movement_leaderboard || [];
  const bests = data?.personal_bests || [];
  const streak = data?.streak || {};
  const movementIQ = iq?.sub_scores?.movement;
  const movementInsight = iq?.movement_insight;
  const movementBreakdown = iq?.score_breakdown?.movement;

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header with Movement IQ score + transparency popover */}
        <div className="flex items-start justify-between gap-4">
          <PageHeader
            tag="Movement Intelligence"
            tagColor="#CCFF00"
            icon={Activity}
            title="Movement"
            subtitle={hasData ? 'How your body moves and how your form is trending across sessions' : 'Complete a few sessions to start tracking progression'}
            testIdPrefix="performance"
          />
          {movementIQ != null && (
            <div className="flex items-center gap-2 shrink-0">
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-secondary">Movement IQ</p>
                <p className="font-secondary font-bold text-3xl" style={{ color: toneHex(scoreBucket(movementIQ).tone) }} data-testid="movement-iq-score">{movementIQ}</p>
              </div>
              <ScoreBreakdownPopover
                breakdown={movementBreakdown}
                label="Movement formula"
                finalScore={movementIQ}
                color="#CCFF00"
                testId="movement-breakdown"
              />
            </div>
          )}
        </div>

        {/* Mobile-only Movement sub-nav — desktop uses the sidebar's expandable Movement group.
            Without this, /workouts /sessions /mobility are unreachable on phones. */}
        <nav
          className="lg:hidden -mx-1 flex items-center gap-2 overflow-x-auto pb-1"
          data-testid="movement-mobile-subnav"
        >
          {[
            { to: '/workouts', label: 'Workouts', icon: Dumbbell, testId: 'subnav-workouts' },
            { to: '/sessions', label: 'Sessions', icon: PlayCircle, testId: 'subnav-sessions' },
            { to: '/mobility', label: 'Mobility', icon: Activity, testId: 'subnav-mobility' },
          ].map(({ to, label, icon: Icon, testId }) => (
            <Link
              key={to}
              to={to}
              data-testid={testId}
              className="shrink-0 flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-zinc-800/60 border border-zinc-700 text-sm font-secondary text-zinc-200 hover:bg-zinc-700/60 hover:text-white transition-colors"
            >
              <Icon className="w-4 h-4 text-primary" />
              {label}
            </Link>
          ))}
        </nav>

        {/* Cross-pillar WHY synthesis — drivers + recommended focus */}
        <MovementWhyPanel
          insight={movementInsight}
          score={movementIQ}
          onActionClick={() => navigate('/chat')}
        />

        <PillarTrendChart pillar="movement" color="#CCFF00" label="Movement" />

        <DistrictEntryButton pillar="movement" accent="#A3E635" name="Movement" />

        <BodyTodayPanel />

        {/* Mobility & ROM trajectory — full chart, matches MQ chart styling */}
        {mobility && (
          <div className="hpme-card p-6 border-l-4" style={{ borderLeftColor: '#CCFF00' }} data-testid="perf-mobility-trajectory">
            <div className="hpme-card-corner" />
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-primary" />
                <h2 className="font-secondary font-bold text-xl">MOBILITY &amp; ROM TRAJECTORY</h2>
              </div>
              <Link to="/mobility" className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="performance-mobility-link">
                Open Mobility map <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
            <p className="text-xs text-zinc-500 uppercase mb-1">Camera-tracked baseline · retest every 28d</p>
            <div className="flex items-baseline gap-3 flex-wrap mb-1">
              <span className="font-secondary font-bold text-3xl" style={{ color: '#CCFF00' }}>
                {mobility.overall_mobility}
              </span>
              <span className="text-zinc-500 text-sm">/100 overall</span>
              {(mobility.asymmetry_flags?.length || 0) > 0 && (
                <span className="text-xs text-amber-300">
                  · {mobility.asymmetry_flags.length} asymmetry flag{mobility.asymmetry_flags.length > 1 ? 's' : ''}
                </span>
              )}
              {(mobility.weakest_regions || []).slice(0, 3).map(k => (
                <span key={k} className="px-2 py-0.5 rounded-sm bg-yellow-500/15 text-yellow-300 text-[10px] uppercase font-semibold">
                  {regionLabel(k)}
                </span>
              ))}
            </div>
            <div className="h-48 min-h-[192px] min-w-0 mt-4">
              {mobilityHistory.length >= 2 ? (
                <ResponsiveContainer width="99%" height="100%">
                  <LineChart
                    data={buildRegionTrend(mobilityHistory)}
                    margin={{ top: 8, right: 12, left: -8, bottom: 0 }}
                  >
                    <XAxis dataKey="date" stroke="#52525B" fontSize={11} />
                    <YAxis stroke="#52525B" fontSize={11} domain={[0, 100]} />
                    <ReferenceLine y={85} stroke="#22C55E" strokeDasharray="3 3" strokeOpacity={0.3} />
                    <ReferenceLine y={55} stroke="#FACC15" strokeDasharray="3 3" strokeOpacity={0.3} />
                    <Tooltip contentStyle={{ backgroundColor: '#18181B', border: '1px solid #27272A', borderRadius: '4px', fontSize: '12px' }} />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    {TREND_LINES.map(l => (
                      <Line key={l.key} type="monotone" dataKey={l.key} name={l.name} stroke={l.color}
                        strokeWidth={l.strong ? 2.5 : 1.5} strokeDasharray={l.strong ? undefined : '2 2'}
                        dot={l.strong ? { fill: l.color, r: 3.5 } : { r: 2 }} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center" data-testid="perf-mobility-empty-trend">
                  <Activity className="w-10 h-10 text-zinc-700 mb-2" />
                  <p className="text-zinc-500 text-sm">Just one baseline so far — your trend appears after the next retest.</p>
                  <p className="text-zinc-600 text-xs mt-1">28-day cadence keeps the data meaningful.</p>
                </div>
              )}
            </div>
            <p className="text-xs text-zinc-500 mt-2">Each dot = one mobility baseline. Dashed lines mark Strong (85) and Limited (55) bands.</p>
          </div>
        )}

        {!hasData ? (
          <div className="hpme-card p-8 text-center">
            <div className="hpme-card-corner" />
            <Activity className="w-16 h-16 text-zinc-500 mx-auto mb-4" />
            <h3 className="font-secondary font-bold text-xl mb-2">NO PROGRESSION DATA YET</h3>
            <p className="text-zinc-500 mb-4 max-w-md mx-auto">
              Once you've completed at least 2 sessions with camera tracking, this page will show your trajectory, movement breakdown, and personal bests.
            </p>
            <Link to="/workouts">
              <button className="hpme-btn-primary px-6 py-3" data-testid="perf-start-workout">
                <Dumbbell className="w-5 h-5 mr-2 inline" /> Browse Workouts
              </button>
            </Link>
          </div>
        ) : (
          <>
            {/* Section 1: Headline trend */}
            <div className="hpme-card p-6 border-l-4" style={{ borderLeftColor: toneHex(currentBucket.tone) }} data-testid="perf-trajectory">
              <div className="hpme-card-corner" />
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                <h2 className="font-secondary font-bold text-xl">MOVEMENT QUALITY TRAJECTORY</h2>
              </div>
              <p className="text-xs text-zinc-500 uppercase mb-1">Last 7 sessions average</p>
              <div className="flex items-baseline gap-3 flex-wrap mb-1">
                <span className={`font-secondary font-bold text-3xl ${toneClass(currentBucket.tone)}`}>
                  {currentBucket.label}
                </span>
                {currentAvg !== null && currentAvg !== undefined && (
                  <span className="text-zinc-400 font-mono text-xl">{Math.round(currentAvg)}/100</span>
                )}
                <span className="text-sm">
                  <DeltaPill value={delta} />
                  {delta !== null && delta !== undefined && (
                    <span className="text-zinc-500 text-xs ml-1">vs prior 7</span>
                  )}
                </span>
              </div>

              <div className="h-48 mt-4">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trend} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                    <XAxis
                      dataKey="date"
                      stroke="#52525B"
                      fontSize={11}
                      tickFormatter={(v) => v ? v.slice(5) : ''}
                    />
                    <YAxis stroke="#52525B" fontSize={11} domain={[0, 100]} />
                    <ReferenceLine y={90} stroke="#22C55E" strokeDasharray="3 3" strokeOpacity={0.3} />
                    <ReferenceLine y={60} stroke="#FACC15" strokeDasharray="3 3" strokeOpacity={0.3} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#18181B', border: '1px solid #27272A', borderRadius: '4px', fontSize: '12px' }}
                      formatter={(value) => [`${value}/100`, scoreBucket(value).label]}
                    />
                    <Line
                      type="monotone"
                      dataKey="score"
                      stroke="#CCFF00"
                      strokeWidth={2}
                      dot={{ fill: '#CCFF00', r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="text-xs text-zinc-500 mt-2">Each dot = one completed session. Dashed lines mark Excellent (90) and Average (60) thresholds.</p>
            </div>

            {/* Section 2: Movement leaderboard */}
            <div className="hpme-card p-6" data-testid="perf-leaderboard">
              <div className="hpme-card-corner" />
              <div className="flex items-center gap-2 mb-1">
                <Dumbbell className="w-5 h-5 text-primary" />
                <h2 className="font-secondary font-bold text-xl">MOVEMENT BREAKDOWN</h2>
              </div>
              <p className="text-zinc-400 text-sm mb-4">
                Movements you've done at least twice — sorted by how well you've been doing them lately.
              </p>
              {leaderboard.length === 0 ? (
                <p className="text-zinc-500 text-sm py-4">No movement has 2+ sessions yet. Repeat a workout to see this section come to life.</p>
              ) : (
                <div className="divide-y divide-zinc-800">
                  {leaderboard.map((m, idx) => {
                    const bk = scoreBucket(m.recent_avg);
                    const bestBk = scoreBucket(m.best);
                    return (
                      <div key={idx} className="py-3 flex items-center gap-4 flex-wrap" data-testid={`leaderboard-row-${idx}`}>
                        <div className="flex-1 min-w-[140px]">
                          <p className="font-secondary font-bold text-base text-zinc-100">{m.exercise}</p>
                          <p className="text-xs text-zinc-500">{m.sessions} session{m.sessions === 1 ? '' : 's'}</p>
                        </div>
                        <div className="min-w-[120px]">
                          <p className="text-[10px] text-zinc-500 uppercase">Recent avg</p>
                          <p className={`font-semibold text-sm ${toneClass(bk.tone)}`}>
                            {bk.label} <span className="text-zinc-500 font-mono text-xs ml-1">{Math.round(m.recent_avg)}/100</span>
                          </p>
                        </div>
                        <div className="min-w-[100px]">
                          <p className="text-[10px] text-zinc-500 uppercase">Best ever</p>
                          <p className={`font-semibold text-sm ${toneClass(bestBk.tone)}`}>
                            <span className="font-mono text-xs">{Math.round(m.best)}/100</span>
                          </p>
                        </div>
                        <div className="min-w-[90px]">
                          <p className="text-[10px] text-zinc-500 uppercase">Trend</p>
                          <DeltaPill value={m.delta} />
                        </div>
                        <div>
                          <Sparkline values={m.last_5} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Section 3: Personal bests */}
            {bests.length > 0 && (
              <div className="hpme-card p-6" data-testid="perf-personal-bests">
                <div className="hpme-card-corner" />
                <div className="flex items-center gap-2 mb-1">
                  <Trophy className="w-5 h-5 text-yellow-400" />
                  <h2 className="font-secondary font-bold text-xl">PERSONAL BESTS</h2>
                </div>
                <p className="text-zinc-400 text-sm mb-4">Your top score on every movement and the day you nailed it.</p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {bests.map((b, idx) => {
                    const bk = scoreBucket(b.score);
                    return (
                      <div
                        key={idx}
                        className="bg-zinc-800/40 p-4 rounded-sm border-l-2"
                        style={{ borderLeftColor: toneHex(bk.tone) }}
                        data-testid={`personal-best-${idx}`}
                      >
                        <p className="font-secondary font-bold text-zinc-100 text-sm uppercase tracking-wide truncate">
                          {b.exercise}
                        </p>
                        <p className={`font-mono text-2xl mt-1 ${toneClass(bk.tone)}`}>
                          {Math.round(b.score)}<span className="text-zinc-500 text-base">/100</span>
                        </p>
                        <p className="text-xs text-zinc-500 mt-1">
                          <span className={`${toneClass(bk.tone)} font-semibold`}>{bk.label}</span>
                          {' · '}
                          {b.date}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Footer streak chip */}
            <div className="hpme-card p-4 flex items-center gap-3" data-testid="perf-streak-strip">
              <Flame className="w-5 h-5 text-orange-400" />
              <p className="text-sm">
                <span className="text-zinc-100 font-semibold">{streak.current_days || 0}-day</span>
                <span className="text-zinc-500"> streak · trained </span>
                <span className="text-zinc-100 font-semibold">{streak.days_trained_last_14 || 0} of last 14</span>
                <span className="text-zinc-500"> days</span>
              </p>
              <Link to="/sessions" className="ml-auto text-xs text-primary hover:underline flex items-center gap-1" data-testid="perf-view-history">
                View history <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Performance;
